// La campana y su panel — ADR-2026-08-31-centro-de-notificaciones.
//
// Un solo componente para los tres lugares donde vive la campana (la fila de cuenta
// de la home, la sidebar del shell y el header flotante de mobile), porque lo que
// cambia entre ellos es la CÁSCARA del botón —una pastilla gris de 34px, un ítem de
// navegación, un círculo blanco flotante— y no el dato: mismo badge, mismo conteo.
// Lo único que difiere es el destino del click: la fila de cuenta abre el panel de
// vistazo; la sidebar y mobile van derecho a `/notificaciones`
// (ADR-2026-08-31-campana-de-sidebar-navega-a-la-pantalla).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useNotificaciones } from '../../hooks/useNotificaciones';
import { FilaNotificacion } from './FilaNotificacion';
import type { Notificacion } from '../../lib/notificaciones';

/** Arriba de esto el badge dice "9+": tres dígitos no entran en una pastilla de 16px. */
const TOPE_BADGE = 9;

export type VarianteCampana = 'encabezado' | 'sidebar' | 'mobile';

/**
 * El número rojo. Va como `<span>` absoluto sobre el botón y no como parte de su
 * contenido: si empujara el layout, la fila de cuenta se movería cada vez que entra
 * una notificación.
 *
 * Rojo y no el azul de marca: el badge tiene que leerse como "esto reclama tu
 * atención" y el azul es el color de todo lo demás en esa fila.
 */
function Badge({
  cantidad,
  borde,
  inline = false,
}: {
  cantidad: number;
  /** Color del aro. Tiene que ser el de la superficie que hay DEBAJO del botón. */
  borde?: string;
  /**
   * `true` = el badge es un elemento más de la fila, alineado a la derecha (el ítem
   * de sidebar expandido). `false` = flota sobre la esquina del control.
   *
   * En la sidebar expandida el badge NO puede ir flotando sobre el ícono: el ítem
   * mide todo el ancho, así que "la esquina" queda a 200px del glifo, y anclarlo al
   * glifo lo tapa por completo (el número queda encima de la campana y no se ve
   * ninguno de los dos). Alineado a la derecha es además lo que hace cualquier
   * sidebar con contadores.
   */
  inline?: boolean;
}) {
  if (cantidad <= 0) return null;
  const texto = cantidad > TOPE_BADGE ? `${TOPE_BADGE}+` : cantidad;
  const base =
    'grid h-[16px] min-w-[16px] place-items-center rounded-full bg-[#E5484D] px-1 text-[10px] font-bold leading-none text-white';
  if (inline) {
    return (
      <span className={`${base} ml-auto shrink-0`} aria-hidden="true">
        {texto}
      </span>
    );
  }
  return (
    <span
      // Sin el aro, el badge se pega al borde del control y los dos colores se
      // tocan. El color lo pasa el consumidor porque cambia según dónde esté
      // montada la campana.
      className={`pointer-events-none absolute -right-1 -top-1 ring-2 ${borde} ${base}`}
      aria-hidden="true"
    >
      {texto}
    </span>
  );
}

/** Dónde se dibuja el panel, en coordenadas de viewport, y cuánto puede medir. */
interface Posicion { top: number; left?: number; right?: number; maxAlto: number }

const ANCHO_PANEL = 380;
/** Alto deseado. El real se recorta a lo que quede de viewport. */
const ALTO_PANEL = 520;
const MARGEN = 8;

export interface CampanaNotificacionesProps {
  variante: VarianteCampana;
  /** Sólo `sidebar`: sin etiqueta cuando la sidebar está colapsada. */
  colapsada?: boolean;
  /**
   * Cómo navegar. El shell de escritorio pasa el suyo para que la pantalla salga con
   * el fade de salida en vez de cortarse de golpe (`conFadeDeSalida`).
   */
  onNavegar?: (ruta: string) => void;
  className?: string;
}

export function CampanaNotificaciones({
  variante,
  colapsada = false,
  onNavegar,
  className = '',
}: CampanaNotificacionesProps) {
  const navigate = useNavigate();
  const { items, sinLeer, cargado, marcarTodas, marcarUna } = useNotificaciones();
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState<Posicion | null>(null);
  const botonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const ir = useCallback(
    (ruta: string) => {
      setAbierto(false);
      if (onNavegar) onNavegar(ruta);
      else navigate(ruta);
    },
    [navigate, onNavegar],
  );

  // Ni en mobile ni en la sidebar la campana abre panel: llevan derecho a
  // `/notificaciones`.
  //
  // En mobile porque un popover de 380px no entra en un teléfono, y la versión
  // "sheet" sería una tercera forma de la misma lista para ganar un tap.
  //
  // En la sidebar porque ahí la campana es un ÍTEM DE NAVEGACIÓN: está en la misma
  // columna que "Centro de ayuda" e "Invitá y ganá", con la misma cáscara, y el
  // usuario que hace click en una fila de esa lista espera cambiar de pantalla, no
  // que le aparezca un panel flotando al costado. El panel sigue existiendo en la
  // fila de cuenta de la home (`encabezado`), que es donde sí se lee como un vistazo
  // rápido colgado de un ícono.
  const vaDirecto = variante === 'mobile' || variante === 'sidebar';

  const alternar = () => {
    if (vaDirecto) { ir('/notificaciones'); return; }
    setAbierto((v) => !v);
  };

  // Posición del panel. Se calcula en `useLayoutEffect` —antes del pintado— para que
  // no se vea aparecer en (0,0) y saltar al lugar correcto.
  useLayoutEffect(() => {
    if (!abierto || !botonRef.current) return;
    const r = botonRef.current.getBoundingClientRect();
    // Sólo queda la variante `encabezado`: es la única que abre panel.
    //
    // Alineado a la DERECHA del botón: la campana vive en el extremo derecho de la
    // fila de cuenta, así que un panel alineado a la izquierda se saldría por fuera
    // del viewport.
    const top = r.bottom + MARGEN;
    setPos({
      top,
      right: Math.max(MARGEN, window.innerWidth - r.right),
      // El MENOR entre el alto deseado y lo que queda de pantalla debajo del botón.
      //
      // Las dos mitades hacen falta. Sin el recorte por viewport, en un portátil de
      // 768px el panel se cortaba contra el borde de abajo y el "Ver todas" —que
      // vive pegado al pie— quedaba fuera de alcance. Sin el tope de `ALTO_PANEL`,
      // en una pantalla grande el panel se estira a 750px y deja de leerse como un
      // popover: pasa a ser una columna que tapa media pantalla. Un panel es un
      // vistazo; el historial largo es `/notificaciones`.
      maxAlto: Math.min(ALTO_PANEL, Math.max(240, window.innerHeight - top - MARGEN)),
    });
  }, [abierto]);

  // Cerrar: click afuera, Escape, scroll o resize. Los dos últimos porque el panel
  // está posicionado en coordenadas de viewport y calculado UNA vez — si la página
  // se mueve debajo, el panel queda flotando lejos de su botón. Cerrarlo es más
  // honesto (y más barato) que recalcular su posición en cada frame de scroll.
  useEffect(() => {
    if (!abierto) return;
    const afuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || botonRef.current?.contains(t)) return;
      setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    const cerrar = () => setAbierto(false);
    // El scroll de la LISTA no cierra el panel — sólo el de la página de atrás.
    //
    // El listener va en fase de CAPTURA porque el scroll no burbujea al `window`:
    // lo emite el contenedor que scrollea, y el shell tiene su propia columna con
    // overflow. Pero capturar en `window` también agarra el scroll de la lista de
    // notificaciones, que es un contenedor con overflow más — así que el panel se
    // cerraba en cuanto el usuario intentaba bajar en él, y con diez notificaciones
    // era imposible llegar a la última.
    //
    // El chequeo es contra el TARGET del evento (el elemento que scrolleó), no
    // contra el puntero: es exactamente la pregunta "¿lo que se movió está adentro
    // del panel?".
    const alScrollear = (e: Event) => {
      const t = e.target as Node | null;
      if (t && panelRef.current?.contains(t)) return;
      setAbierto(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', tecla);
    window.addEventListener('resize', cerrar);
    window.addEventListener('scroll', alScrollear, true);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', tecla);
      window.removeEventListener('resize', cerrar);
      window.removeEventListener('scroll', alScrollear, true);
    };
  }, [abierto]);

  const abrirNotificacion = (n: Notificacion) => {
    void marcarUna(n.id);
    // Sin ruta = no es accionable: se marca como leída y el panel queda abierto. Que
    // el panel se cierre sin llevar a ningún lado se lee como que el tap falló.
    if (n.ruta) ir(n.ruta);
  };

  // El badge sólo se dibuja con el dato ya cargado: mostrar "0" —o peor, el badge
  // apareciendo un segundo después de que la fila se dibujó— es un parpadeo gratis.
  const badge = cargado ? sinLeer : 0;
  const etiqueta =
    sinLeer > 0 ? `Notificaciones (${sinLeer} sin leer)` : 'Notificaciones';

  const boton = (() => {
    if (variante === 'sidebar') {
      return (
        <button
          ref={botonRef}
          type="button"
          onClick={alternar}
          title={colapsada ? etiqueta : undefined}
          aria-label={etiqueta}
          className={`relative flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] text-black/60 transition-colors hover:bg-black/[0.03] hover:text-black/85 ${
            colapsada ? 'mx-auto h-9 w-9 justify-center' : 'w-full gap-2.5 px-2.5'
          } ${className}`}
        >
          <span className="material-symbols-rounded shrink-0 text-[19px]">notifications</span>
          {!colapsada && 'Notificaciones'}
          {/* Colapsada, el badge cuelga de la esquina del ícono (no hay otro lado);
              expandida, va alineado a la derecha de la fila como un contador. */}
          <Badge cantidad={badge} borde="ring-white" inline={!colapsada} />
        </button>
      );
    }

    if (variante === 'mobile') {
      // 36px y blanco translúcido: las mismas medidas y el mismo tratamiento que la
      // pastilla de créditos y el acceso a WhatsApp, que son sus vecinos en el chrome
      // flotante sobre el hero.
      return (
        <button
          ref={botonRef}
          type="button"
          onClick={alternar}
          aria-label={etiqueta}
          className={`relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/95 text-brand-black/85 shadow-md ring-1 ring-black/[0.04] backdrop-blur-md active:opacity-70 ${className}`}
        >
          <span className="material-symbols-rounded text-[20px]">notifications</span>
          {/* El aro del badge va blanco: acá el botón flota sobre la foto del hero,
              y sin aro el rojo se mezcla con lo que haya detrás. */}
          <Badge cantidad={badge} borde="ring-white" />
        </button>
      );
    }

    // `encabezado`: cuadrado de 34px, el mismo alto que el resto de la fila de
    // cuenta. Sin etiqueta —como Whatsapp— porque la campana es de los pocos íconos
    // que no necesitan una (ADR-2026-08-31-el-enfasis-de-la-fila-pasa-a-invita-y-gana).
    return (
      <button
        ref={botonRef}
        type="button"
        onClick={alternar}
        title={etiqueta}
        aria-label={etiqueta}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        className={`relative flex h-[34px] w-[34px] items-center justify-center rounded-xl bg-white text-black/60 ring-1 ring-black/[0.08] transition-colors hover:text-black/85 hover:ring-black/[0.14] ${className}`}
      >
        <span className="material-symbols-rounded text-[19px]">notifications</span>
        <Badge cantidad={badge} borde="ring-[#F7F7F8]" />
      </button>
    );
  })();

  return (
    <>
      {boton}
      {abierto && pos &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notificaciones"
            // `fixed` + portal al body: dentro del árbol, el panel lo recortarían el
            // scroll de la sidebar y el `overflow` de la columna de contenido.
            className="fixed z-[400] flex flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-[0_12px_40px_rgba(2,6,43,0.16)]"
            style={{
              top: pos.top,
              left: pos.left,
              right: pos.right,
              width: ANCHO_PANEL,
              maxHeight: pos.maxAlto,
            }}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-black/[0.06] px-4 py-3">
              <p className="text-[14px] font-[600] text-black/85">Notificaciones</p>
              {/* "Marcar todas" sólo cuando hay algo que marcar: un botón permanente
                  que la mitad del tiempo no hace nada es ruido. */}
              {sinLeer > 0 && (
                <button
                  type="button"
                  onClick={() => void marcarTodas()}
                  className="text-[12.5px] font-[500] text-brand-primary transition-opacity hover:opacity-70"
                >
                  Marcar todas
                </button>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                  <span className="material-symbols-rounded mb-2 text-[32px] text-black/20">
                    notifications_off
                  </span>
                  <p className="text-[13px] font-[600] text-black/60">Todo al día</p>
                  <p className="mt-1 text-[12.5px] leading-[17px] text-black/40">
                    Acá te vamos a avisar cuando terminen tus generaciones y cuando
                    alguien acepte tu invitación.
                  </p>
                </div>
              ) : (
                items.map((n) => (
                  <FilaNotificacion key={n.id} notificacion={n} onAbrir={abrirNotificacion} />
                ))
              )}
            </div>

            {/* El pie va SIEMPRE, incluso con la lista vacía: es el acceso al
                historial, y una cuenta sin nada pendiente igual puede querer mirar
                lo que ya leyó. */}
            <button
              type="button"
              onClick={() => ir('/notificaciones')}
              className="shrink-0 border-t border-black/[0.06] py-2.5 text-center text-[12.5px] font-[500] text-brand-primary transition-colors hover:bg-black/[0.02]"
            >
              Ver todas
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

export default CampanaNotificaciones;
